export const TIME_OF_DAY = 'time-of-day'
export const PROGRESS_BAR = 'progress-bar'
export const PROGRESS_BAR_HEIGHT = 'progress-bar-height'
export const LAST_FIVE_SECONDS = 'last-five-seconds'
export const OVER_UNDER = 'over-under'
export const LOGO = 'logo'
export const MIRROR = 'mirror'
export const RUNDOWN_TITLE = 'rundown-title'
export const ZOOM_LEVEL = 'zoom'
export const CUE_BACKGROUND_COLORS = 'cue-background-colors'
export const CUE_NUMBERS = 'cue-numbers'
export const TITLES = 'titles'
export const SUBTITLES = 'subtitles'
export const START_END_TIMES = 'start-end-times'
export const START_TIMES = 'start-times'
export const DURATIONS = 'durations'
export const END_TIMES = 'end-times'
export const START_AT_CUE = 'start-at-cue'
export const HEADER_POSITION = 'header-position'
export const LARGE_TIME_OF_DAY = 'large-time-of-day'
export const BACKGROUND_COLOR = 'background-color'
export const NEXT_CUE = 'next-cue'
export const CUE_TIMER = 'cue-timer'
export const LAYOUT = 'layout'

export const ON = 'on'
export const OFF = 'off'
export const VERTICAL = 'vertical'
export const HORIZONTAL = 'horizontal'
export const TOP = 'top'
export const BOTTOM = 'bottom'
export const SMALL = 'small'
export const MEDIUM = 'medium'
export const LARGE = 'large'
export const BLACK = 'black'

export const LIST = 'list'
export const SINGLE = 'single'
export const PROMPTER = 'prompter'

// Layout-specific options
export const COLUMN = 'column'
export const COLUMNS = 'columns'

/**
 * Config for advanced Output options
 *
 * Adding an option here? Three rules, and they are worth pushing back on in
 * review rather than quietly implementing around (John, this means you):
 *
 * 1. `on` always means visible, `off` always means hidden. Never add an inverted
 *    option like `hide-titles` where `on` hides something. Half the options
 *    meaning one thing and half the other is how users end up guessing.
 * 2. Name it after the thing it controls, as a plain noun matching its neighbours:
 *    `titles`, `cue-numbers`, `durations`. Not a verb, not a `show-`/`hide-`
 *    prefix on some options but not others.
 * 3. Pick the default deliberately: core rundown content defaults to `on`, opt-in
 *    extras default to `off`. Then compare it the matching way at every call
 *    site (see the rule documented on getOption).
 *
 * An option that breaks these is worse than no option, because the URL is the
 * only interface users have here and there is no UI hinting what a value does.
 */
export const OUTPUT_OPTIONS_CONFIG = {
  //
  // TODO: Add layout-specific options to config / docs?
  //
  [RUNDOWN_TITLE]: {
    name: RUNDOWN_TITLE,
    default: ON,
  },
  [LOGO]: {
    name: LOGO,
    default: ON,
  },
  [TIME_OF_DAY]: {
    name: TIME_OF_DAY,
    default: ON,
  },
  [OVER_UNDER]: {
    name: OVER_UNDER,
    default: ON,
  },
  [PROGRESS_BAR]: {
    name: PROGRESS_BAR,
    default: ON,
  },
  [LAST_FIVE_SECONDS]: {
    name: LAST_FIVE_SECONDS,
    default: ON,
  },
  [MIRROR]: {
    name: MIRROR,
    default: OFF,
  },
  [CUE_BACKGROUND_COLORS]: {
    name: CUE_BACKGROUND_COLORS,
    default: ON,
  },
  [CUE_NUMBERS]: {
    name: CUE_NUMBERS,
    default: ON,
  },
  [TITLES]: {
    name: TITLES,
    default: ON,
  },
  [SUBTITLES]: {
    name: SUBTITLES,
    default: ON,
  },
  [START_TIMES]: {
    name: START_TIMES,
    default: OFF,
  },
  [DURATIONS]: {
    name: DURATIONS,
    default: ON,
  },
  [END_TIMES]: {
    name: END_TIMES,
    default: OFF,
  },
  [HEADER_POSITION]: {
    name: HEADER_POSITION,
    default: TOP,
  },
  [BACKGROUND_COLOR]: {
    name: BACKGROUND_COLOR,
    default: BLACK,
  },
  [LARGE_TIME_OF_DAY]: {
    name: LARGE_TIME_OF_DAY,
    default: OFF,
  },
  [NEXT_CUE]: {
    name: NEXT_CUE,
    default: OFF,
  },
  [CUE_TIMER]: {
    name: CUE_TIMER,
    default: ON,
  },
  [LAYOUT]: {
    name: LAYOUT,
    default: LIST,
  },
  /** Deprecated options */
  // [PROGRESS_BAR_HEIGHT]: {
  //   name: PROGRESS_BAR_HEIGHT,
  //   default: SMALL,
  // },
  // [ZOOM_LEVEL]: {
  //   name: ZOOM_LEVEL,
  //   default: 1,
  // },
  // [START_END_TIMES]: {
  //   name: START_END_TIMES,
  //   default: ON,
  // },
  // [START_AT_CUE]: {
  //   name: START_AT_CUE,
  //   default: '',
  // },
} as const

export type OutputOptionKey = keyof typeof OUTPUT_OPTIONS_CONFIG

/**
 * UI metadata for the config controls. Storage is unchanged: controls still
 * write `key=value` pairs to `settings.outputConfig`.
 *
 * `control`: 'toggle' | 'select' (over `choices`) | 'color' | 'axes' (mirror).
 * `layouts`: layouts the option affects; omitted means all. Outside them the
 * control is disabled, not hidden. LAYOUT is absent, the picker owns it.
 */
export const OUTPUT_OPTIONS_UI = {
  [RUNDOWN_TITLE]: {
    label: 'Rundown title',
    control: 'toggle',
    group: 'Header',
  },
  [LOGO]: { label: 'Logo', control: 'toggle', group: 'Header' },
  [TIME_OF_DAY]: { label: 'Time of day', control: 'toggle', group: 'Header' },
  [CUE_TIMER]: { label: 'Cue timer', control: 'toggle', group: 'Header' },
  [OVER_UNDER]: { label: 'Over / under', control: 'toggle', group: 'Header' },
  [PROGRESS_BAR]: { label: 'Progress bar', control: 'toggle', group: 'Header' },
  [LAST_FIVE_SECONDS]: {
    label: 'Last five seconds',
    control: 'toggle',
    group: 'Header',
  },
  [HEADER_POSITION]: {
    label: 'Header position',
    control: 'select',
    group: 'Header',
    choices: [
      { value: TOP, label: 'Top' },
      { value: BOTTOM, label: 'Bottom' },
    ],
  },

  [CUE_NUMBERS]: {
    label: 'Cue numbers',
    control: 'toggle',
    group: 'Cues',
    layouts: [LIST],
  },
  [TITLES]: {
    label: 'Titles',
    control: 'toggle',
    group: 'Cues',
    layouts: [LIST],
  },
  [SUBTITLES]: {
    label: 'Subtitles',
    control: 'toggle',
    group: 'Cues',
    layouts: [LIST],
  },
  [CUE_BACKGROUND_COLORS]: {
    label: 'Cue background colours',
    control: 'toggle',
    group: 'Cues',
    layouts: [LIST],
  },
  [DURATIONS]: {
    label: 'Durations',
    control: 'toggle',
    group: 'Cues',
    layouts: [LIST],
  },
  [START_TIMES]: {
    label: 'Start times',
    control: 'toggle',
    group: 'Cues',
    layouts: [LIST, SINGLE],
  },
  [END_TIMES]: {
    label: 'End times',
    control: 'toggle',
    group: 'Cues',
    layouts: [LIST, SINGLE],
  },
  [NEXT_CUE]: {
    label: 'Next cue',
    control: 'toggle',
    group: 'Cues',
    layouts: [SINGLE],
  },
  [LARGE_TIME_OF_DAY]: {
    label: 'Large time of day',
    control: 'toggle',
    group: 'Cues',
    layouts: [SINGLE],
  },

  [BACKGROUND_COLOR]: {
    label: 'Background colour',
    control: 'color',
    group: 'Display',
  },
  [MIRROR]: { label: 'Mirror', control: 'axes', group: 'Display' },
} as const

/** `mirror` takes a comma-separated list, so both axes can be flipped at once. */
export const MIRROR_AXES = [
  { value: VERTICAL, label: 'Vertically' },
  { value: HORIZONTAL, label: 'Horizontally' },
] as const

/** Groups in display order, with whether each starts expanded. */
export const OUTPUT_OPTION_GROUPS = [
  { name: 'Display', defaultOpen: true },
  { name: 'Header', defaultOpen: false },
  { name: 'Cues', defaultOpen: false },
] as const
